import { Component, Prop, h } from '@stencil/core';

@Component({
  tag: 'a1-headline',
  styleUrl: 'a1-headline.scss',
  scoped: true,
})
export class A1Headline {
  /**
   * Headline variant.
   */
  @Prop({ reflect: true }) type: 'primary' | 'secondary' = 'primary';

  /**
   * Headline font size. Default is 4xl.
   */
  @Prop({ reflect: true }) size: '6xl' | '5xl' | '4xl' | '3xl' | '2xl' | 'xl' | 'l' | 'm' | 's' = '4xl';

  /**
   * HTML element to be used. Default is h3.
   */
  // prettier-ignore
  @Prop({ reflect: true }) htmlTag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'div' | 'span' = 'h2';

  /**
   * Semantic HTML value (aria-level) to be assigned when div, span or p element is used as a headline. Default is 3.
   * This is used for screen readers and SEO.
   */
  // prettier-ignore
  @Prop({ reflect: true }) headingLevel: 1 | 2 | 3 | 4 | 5 | 6 = 2;

  /**
   * Text label for headline. Use `{accent}` in templates, `<b>` or `<em>` in HTML, to define the portion of the text to be accent.
   */
  @Prop({ reflect: true }) text: string = 'Headline text goes here';

  /**
   * Color for the accent text.
   */
  // prettier-ignore
  @Prop({ reflect: true }) accent: 'yellow' | 'orange' | 'red' | 'purple' | 'aqua' | 'blue' | 'teal' | 'green' | null = null;

  /**
   * Toggle for inverse appearance.
   */
  @Prop({ reflect: true }) inverse: boolean = false;

  private scopeText(text: string) {
    return text
      .replace(/{accent}/g, '<em>')
      .replace(/{\/accent}/g, '</em>')
      .replace(/<b>/g, '<em>')
      .replace(/<\/b>/g, '</em>');
  }

  render() {
    const Tag = this.htmlTag;
    const isHeading = this.htmlTag.startsWith('h');
    const isAccent = this.text.includes('{accent}') || this.text.includes('<b>') || this.text.includes('<em>');
    const accent = isAccent && this.accent !== null ? `headline-accent--${this.accent}` : '';
    const classes = {
      [this.type]: true,
      [`headline-${this.size}`]: true,
      inverse: this.inverse,
      [accent]: true,
    };

    return (
      <Tag
        class={classes}
        role={isHeading ? undefined : 'heading'}
        aria-level={isHeading ? undefined : this.headingLevel}
        innerHTML={isAccent ? this.scopeText(this.text) : undefined}
      >
        {!isAccent && this.text}
      </Tag>
    );
  }
}
